import { Accordion, Button, Heading, Img, LottiePlayer } from '@okam/brand-ui/index'
import { WysiwygBlock } from '@okam/shared-ui/index'
import { useTranslation } from 'next-i18next'
import tw, { css, theme } from 'twin.macro'
import { useFragment } from 'gql'
import { trackImportantClick } from 'gtm-analytics/tracking'
import { toDirectusUrl } from 'lib/directus'
import withForwardedRefSection from 'lib/utils/withForwardedRefSection'
import { blockFaqFragment, colorSchemeFragment } from 'query/single-page/fragment'
import type { TPetParentsCornerFaqBlock } from '../../types'

/* eslint-disable-next-line react/display-name */
const FaqPetParentsCornerBlock = (props: TPetParentsCornerFaqBlock) => {
  const { title, colors, qnaList } = useFragment(blockFaqFragment, props)
  const { frItem } = props

  const blockColors = useFragment(colorSchemeFragment, colors)

  const qnaListTyped = qnaList as Array<{ question: string; answer: string }>
  let frQnaList: Array<{ question: string; answer: string }>

  if (frItem?.type === 'block_faq') {
    const { qnaList: frQna } = useFragment(blockFaqFragment, frItem) /* eslint-disable-line */
    frQnaList = frQna as Array<{ question: string; answer: string }>
  }

  const { t } = useTranslation('common')

  const animationSRCID = t('ANIMATION.LCDH_ID')
  const animationSRCFILE = t('ANIMATION.LCDH_FILE')
  const seeEverythingLabel = t('BUTTON.SEE_ALL')
  const faqURL = t('URL.FAQ')

  return (
    <div
      css={[
        css`
          background-color: ${blockColors?.background};
          ${tw`flex flex-col font-semibold pb-8 lg:pb-32 md:pt-48 overflow-hidden`}
        `,
      ]}
    >
      <div>
        <div tw="flex flex-col lg:flex-row relative">
          <div tw="flex flex-col items-start px-6 md:px-20 grow z-20 lg:pr-0 lg:w-[50%] 3xl:w-[60%]">
            <Heading as="h2" variant="h3" tw="pb-4">
              {title}
            </Heading>
            <Button href={faqURL} as="a" buttonStyle="hollow" icon="ArrowRight" variant="textIcon">
              {seeEverythingLabel}
            </Button>
            <div tw="pt-8 flex flex-col gap-5 lg:w-[50%] 3xl:w-[60%]">
              {qnaListTyped.map((qna, index) => {
                const ariaLabel = t('ARIA_LABEL.ACCORDION', { title: qna.question })
                return (
                  <div key={qna.question}>
                    <Accordion
                      ariaLabel={ariaLabel}
                      buttonColor={theme('colors.primary[3]')}
                      color="#0C8925"
                      hoverColor="transparent"
                      id={qna.question}
                      title={qna.question}
                      onClick={() =>
                        trackImportantClick({
                          click_type: 'accordéon',
                          interaction_location: 'page',
                          link_url: null,
                          link_text: qna.question ?? '',
                          link_text_fr: frQnaList?.[index]?.question ?? '',
                        })
                      }
                    >
                      <WysiwygBlock content={qna.answer} />
                    </Accordion>
                  </div>
                )
              })}
            </div>
          </div>
          <div tw="lg:absolute right-0 -bottom-8 z-10">
            <LottiePlayer src={toDirectusUrl(animationSRCID, animationSRCFILE) ?? ''} autoplay loop width="100%" />
          </div>
          <div
            css={css`
              clip-path: circle(50% at 50% 50%);
            `}
            tw="absolute -bottom-8 lg:-bottom-40 md:-right-32 md:-bottom-80"
          >
            <Img
              src="/shared-assets/images/texture-triangle.png"
              priority
              alt={t('IMAGE.TEXTURE')}
              width={1200}
              height={1200}
              customCSS="height: auto; width: auto;"
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default withForwardedRefSection<TPetParentsCornerFaqBlock>(FaqPetParentsCornerBlock)
